/**
 * AgentModal.tsx — the API / Code agent settings editor.
 *
 * Layout (per spec):
 *   ┌──────────────────────────────────────────────────────┐
 *   │ [ API ][ Code ]                                 [✕]  │  ← tabs ARE the header (no title)
 *   ├──────────────────────────────────────────────────────┤
 *   │  target  — method/url (API) or file from library(Code)│
 *   │  params  — pills: all (default) + host params         │
 *   │  actions — grants: emitEvent / triggerWorkflow        │
 *   │  linked doc — optional, opens LibraryModal (API only) │
 *   │  input   — read-only                                  │
 *   ├──────────────────────────────────────────────────────┤
 *   │                              [ Delete agent ]         │  ← FOOTER
 *   └──────────────────────────────────────────────────────┘
 *
 * File / linked-document selection opens the REAL LibraryModal (the same
 * full-page searchable picker used everywhere else) — not an inline combobox.
 * The adapter is passed in so LibraryModal can run its own live search.
 *
 * The agent runner executes on the backend relay path (docs/AGENT_SYSTEM.md,
 * docs/TRAINING.md); this modal captures configuration — target, the knob
 * fence (outputMapping), and the action fence (actionGrants).
 */
import { useMemo, useState } from 'react';
import { FileText, X } from '@phosphor-icons/react';

import type { AgentConfig } from '../engine/types';
import { Modal } from './Modal';
import { LibraryModal } from './LibraryModal';
import { SourceFilePicker } from './SourceFilePicker';
import { uuid } from '../library/ids';
import type { LibraryAdapter } from '../library/LibraryAdapter';
import type { RemoteProjectAdapter } from '../projects/RemoteProjectAdapter';

/** A param key the agent may control, with a human label. */
export interface AgentParamOption {
  /** The param key (e.g. "capacity"). Display + internal id within the host. */
  key: string;
  label: string;
  /** The full outputMapping path for this param: `<nodeId>.<key>` for a node,
   *  `e:<edgeId>.<key>` for an edge. This is what the capability fence
   *  matches against. Built by the caller (NodeModal/EdgeModal) which knows
   *  the host id. */
  path: string;
}

/** Describes the host (node or edge) so the modal stays host-agnostic. */
export interface AgentHost {
  /** Title shown in the modal header (e.g. "Kitchen" or "A → B"). */
  title: string;
  /** Param keys the agent may control — the pills are built from this. */
  paramOptions: AgentParamOption[];
  /** Read-only description of what flows IN to the agent. */
  inputDescription: string;
  /** Where this agent attaches. `'local'` (default) = the host node/edge;
   *  `'global'` = bundle-level (the agent sees the full tick + params). The
   *  modal writes `scope` on the config so the runner builds the right
   *  context. Phase D. */
  scope?: 'local' | 'global';
}

interface AgentModalProps {
  host: AgentHost;
  /** Current agent config (or undefined when none is configured yet). */
  config?: AgentConfig;
  editable: boolean;
  /** Library adapter — file / linked-document pickers open the real
   *  LibraryModal against this adapter (live search). */
  libraryAdapter?: LibraryAdapter;
  /** Project backend for the Code-tab source-file picker. When set (with
   *  `projectId`), the Code tab's file picker lists the project's `src/*.py`
   *  via SourceFilePicker; otherwise it falls back to the library picker or
   *  free text. Source files only exist for backend (remote) projects. */
  projectAdapter?: RemoteProjectAdapter;
  /** The project id whose `src/` to list in the Code-tab picker. */
  projectId?: string;
  onClose: () => void;
  /** Commit the next config. Pass undefined to clear. */
  onChange: (config: AgentConfig | undefined) => void;
}

type Tab = 'api' | 'code';

export function AgentModal({
  host,
  config,
  editable,
  libraryAdapter,
  projectAdapter,
  projectId,
  onClose,
  onChange,
}: AgentModalProps): JSX.Element {
  // Seed a fresh config when opening for the first time. mode defaults to 'api';
  // scope defaults to the host's scope (or 'local' when unspecified). A NEW
  // agent gets a minted uuid id — the identity key its decisions/logs carry
  // (the activity log groups on it); legacy configs keep deriving from the
  // attachment point.
  const initial = config ?? {
    id: uuid(),
    target: '',
    mode: 'api' as Tab,
    scope: host.scope ?? 'local',
  };
  const [draft, setDraft] = useState<AgentConfig>(initial);
  const [tab, setTab] = useState<Tab>(draft.mode ?? 'api');
  // Which picker is open: 'sourceFile' (Code-tab project src/), 'linkedDoc'
  // (API-tab linked document via LibraryModal), 'target' (legacy library target),
  // or null when closed.
  const [picker, setPicker] = useState<'sourceFile' | 'target' | 'linkedDoc' | null>(null);

  const commit = (next: AgentConfig) => {
    setDraft(next);
    onChange(next);
  };
  const switchTab = (next: Tab) => {
    setTab(next);
    commit({ ...draft, mode: next });
  };

  // ─── Output pills ──────────────────────────────────────────────────────
  // Phase C capability fence: an empty outputMapping = READ-ONLY (the agent
  // may not write any knob). "All params" explicitly enumerates every path
  // into the map. Individual pills toggle individual paths. This replaces the
  // old "empty = all" unsafe default.
  const controlled = useMemo(() => {
    const m = draft.outputMapping ?? {};
    return new Set(Object.keys(m));
  }, [draft.outputMapping]);
  const allPaths = useMemo(() => host.paramOptions.map((p) => p.path), [host.paramOptions]);
  const allSelected = allPaths.length > 0 && allPaths.every((p) => controlled.has(p));
  const readOnly = controlled.size === 0;

  const selectAll = () => {
    if (!editable) return;
    // Enumerate every path explicitly — NOT an empty map (empty = read-only now).
    const nextMap: Record<string, string> = {};
    for (const p of allPaths) nextMap[p] = p;
    commit({ ...draft, outputMapping: nextMap });
  };
  const selectReadOnly = () => {
    if (!editable) return;
    commit({ ...draft, outputMapping: undefined });
  };
  const toggleParam = (path: string) => {
    if (!editable) return;
    const cur = new Set(controlled);
    if (cur.has(path)) cur.delete(path);
    else cur.add(path);
    const nextMap: Record<string, string> = {};
    for (const k of cur) nextMap[k] = k;
    commit({ ...draft, outputMapping: Object.keys(nextMap).length > 0 ? nextMap : undefined });
  };

  // ─── Action grants (the action-bus fence — docs/TRAINING.md §2) ────────
  // Non-param actions: emitEvent (inject into the model's event queue) and
  // triggerWorkflow (start a durable workflow run). A grant is bare
  // ("emitEvent" — any target) or specific ("emitEvent:demand_spike"). An
  // empty list grants nothing — same conservative default as outputMapping.
  const grants = draft.actionGrants ?? [];
  const [grantInput, setGrantInput] = useState('');
  const addGrant = (raw: string) => {
    if (!editable) return;
    const g = raw.trim();
    if (!/^(emitEvent|triggerWorkflow)(:[\w.-]+)?$/.test(g)) return;
    if (!grants.includes(g)) commit({ ...draft, actionGrants: [...grants, g] });
    setGrantInput('');
  };
  const removeGrant = (g: string) => {
    if (!editable) return;
    const next = grants.filter((x) => x !== g);
    commit({ ...draft, actionGrants: next.length > 0 ? next : undefined });
  };

  // ─── Target + linked document ──────────────────────────────────────────
  const setTarget = (target: string) => commit({ ...draft, target });
  const setLinkedDoc = (linkedDocumentId: string) =>
    commit({ ...draft, linkedDocumentId: linkedDocumentId || undefined });

  const clear = () => {
    if (!editable) return;
    onChange(undefined);
    onClose();
  };

  const hasLibrary = !!libraryAdapter;
  // Code-tab source-file picker is available only for backend projects (a
  // project id + the remote adapter that lists its src/ files).
  const hasProject = !!projectAdapter && !!projectId;

  // The tabs ARE the header — no title text, no icon. Just the tab strip.
  const tabs = (
    <div className="agent-tabs agent-tabs-header" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={tab === 'api'}
        className={`agent-tab${tab === 'api' ? ' on' : ''}`}
        onClick={() => switchTab('api')}
      >API</button>
      <button
        type="button"
        role="tab"
        aria-selected={tab === 'code'}
        className={`agent-tab${tab === 'code' ? ' on' : ''}`}
        onClick={() => switchTab('code')}
      >Code</button>
    </div>
  );

  // Footer: Delete agent (only when an agent is configured + editable).
  const footer = editable && draft.target ? (
    <button type="button" className="btn btn-danger" onClick={clear}>
      Delete agent
    </button>
  ) : undefined;

  return (
    <Modal headerLeft={tabs} footer={footer} onClose={onClose}>
      {/* ─── Target ──────────────────────────────────────────────────────
          API tab  → method name or URL (free text).
          Code tab → source file from the project's src/ (SourceFilePicker)
                     when a project backend is wired; else the library picker
                     (LibraryModal); else free text. The chosen dotted module
                     path becomes `AgentConfig.target`. */}
      <section className="modal-section">
        <div className="modal-subhead">{tab === 'api' ? 'Target' : 'File'}</div>
        {/* Display name (optional) — the activity log's sidebar label. */}
        <label className="modal-field">
          <input
            type="text"
            className="text-input"
            placeholder="agent name (shown in the activity log)"
            value={draft.name ?? ''}
            disabled={!editable}
            onChange={(e) => commit({ ...draft, name: e.target.value.trim() || undefined })}
          />
        </label>
        {tab === 'api' ? (
          <label className="modal-field">
            <input
              type="text"
              className="text-input"
              placeholder="method name or URL"
              value={draft.target}
              disabled={!editable}
              onChange={(e) => setTarget(e.target.value)}
            />
          </label>
        ) : hasProject ? (
          <FileField
            label={draft.target || ''}
            placeholder="— pick a source file —"
            disabled={!editable}
            onPick={() => setPicker('sourceFile')}
            onClear={() => setTarget('')}
          />
        ) : hasLibrary ? (
          <FileField
            label={draft.target || ''}
            placeholder="— pick a file —"
            disabled={!editable}
            onPick={() => setPicker('target')}
            onClear={() => setTarget('')}
          />
        ) : (
          <label className="modal-field">
            <input
              type="text"
              className="text-input"
              placeholder="file path or id"
              value={draft.target}
              disabled={!editable}
              onChange={(e) => setTarget(e.target.value)}
            />
          </label>
        )}

        {/* Linked document — API tab only, optional. Opens LibraryModal. */}
        {tab === 'api' && (
          <>
            <div className="modal-subhead" style={{ marginTop: 4 }}>Linked document</div>
            {hasLibrary ? (
              <FileField
                label={draft.linkedDocumentId ?? ''}
                placeholder="— none —"
                disabled={!editable}
                onPick={() => setPicker('linkedDoc')}
                onClear={() => setLinkedDoc('')}
              />
            ) : (
              <label className="modal-field">
                <input
                  type="text"
                  className="text-input"
                  placeholder="document id"
                  value={draft.linkedDocumentId ?? ''}
                  disabled={!editable}
                  onChange={(e) => setLinkedDoc(e.target.value)}
                />
              </label>
            )}
          </>
        )}
      </section>

      {/* ─── Output: parameter selector pills ────────────────────────────
          Phase C capability fence. "Read-only" (default) = no writes.
          "All" = enumerate every path. Individual pills toggle one path each. */}
      <section className="modal-section">
        <div className="modal-subhead">Parameters (agent output)</div>
        <div className="group-pills">
          <button
            type="button"
            className={`group-pill${readOnly ? ' on' : ''}`}
            style={readOnly ? { background: 'var(--ink-muted)', borderColor: 'var(--ink-muted)' } : undefined}
            onClick={selectReadOnly}
            disabled={!editable}
            title="No writes allowed — the agent may only observe + annotate"
          >read-only</button>
          {host.paramOptions.length > 0 && (
            <button
              type="button"
              className={`group-pill${allSelected ? ' on' : ''}`}
              style={allSelected ? { background: 'var(--ink-muted)', borderColor: 'var(--ink-muted)' } : undefined}
              onClick={selectAll}
              disabled={!editable}
              title="Allow writing every param on this host"
            >all</button>
          )}
          {host.paramOptions.map((p) => {
            const on = controlled.has(p.path);
            return (
              <button
                key={p.path}
                type="button"
                className={`group-pill${on ? ' on' : ''}`}
                style={on ? { background: 'var(--accent, #4c72b0)', borderColor: 'var(--accent, #4c72b0)' } : undefined}
                title={p.label}
                onClick={() => toggleParam(p.path)}
                disabled={!editable}
              >{p.label}</button>
            );
          })}
        </div>
        {host.paramOptions.length === 0 && (
          <div className="param-hint">No params on this host — the agent is read-only by default. Add params to the host to allow writes.</div>
        )}
        {/* Magnitude bound: writes are always clamped to each param's
            declared min/max; maxDeltaPerTick additionally caps how far one
            write may move a knob from its current value. */}
        <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
          <span className="param-hint" style={{ padding: 0 }}>Max Δ per write</span>
          <input
            type="number"
            className="text-input"
            style={{ width: 110 }}
            value={draft.maxDeltaPerTick ?? ''}
            placeholder="∞"
            disabled={!editable}
            onChange={(e) => {
              const v = e.target.valueAsNumber;
              commit({ ...draft, maxDeltaPerTick: Number.isFinite(v) ? v : undefined });
            }}
          />
        </div>
        <div className="param-hint">
          Agent writes are always clamped to each param's declared min/max; this
          additionally caps how far one write may move a knob from its current
          value. Each bound applied shows up in the decisions feed.
        </div>
      </section>

      {/* ─── Actions: the non-param action grants ─────────────────────────
          The action bus (docs/TRAINING.md §2). Grants are typed pills:
          bare kind = any target, kind:target = one specific target. */}
      <section className="modal-section">
        <div className="modal-subhead">Actions (beyond params)</div>
        <div className="group-pills">
          {grants.length === 0 && (
            <span className="param-hint" style={{ padding: 0 }}>
              none — the agent may not emit events or trigger workflows
            </span>
          )}
          {grants.map((g) => (
            <button
              key={g}
              type="button"
              className="group-pill on"
              style={{ background: 'var(--accent, #4c72b0)', borderColor: 'var(--accent, #4c72b0)' }}
              title="Remove this grant"
              onClick={() => removeGrant(g)}
              disabled={!editable}
            >{g} ×</button>
          ))}
        </div>
        {editable && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input
              type="text"
              className="text-input"
              style={{ flex: 1 }}
              placeholder="emitEvent · emitEvent:type · triggerWorkflow · triggerWorkflow:name"
              value={grantInput}
              onChange={(e) => setGrantInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addGrant(grantInput);
                }
              }}
            />
            <button
              type="button"
              className="btn"
              onClick={() => addGrant(grantInput)}
              disabled={!/^ *(emitEvent|triggerWorkflow)(:[\w.-]+)? *$/.test(grantInput)}
              title="Grant this action"
            >Add</button>
          </div>
        )}
        <div className="param-hint">
          Events land in the model's event queue (the ingest channel); workflow
          triggers start a durable run. A bare kind allows any target.
        </div>
      </section>

      {/* ─── Input (read-only) ─────────────────────────────────────────── */}
      <section className="modal-section">
        <div className="modal-subhead">Input</div>
        <div className="param-hint" style={{ padding: 0 }}>
          {host.inputDescription}
        </div>
      </section>

      {/* SourceFilePicker — the Code-tab project source-file picker. The chosen
          dotted module path writes to `target`. Only rendered when a project
          backend is wired; otherwise the Code tab falls back to LibraryModal/free text. */}
      {picker === 'sourceFile' && hasProject && (
        <SourceFilePicker
          projectId={projectId!}
          projectAdapter={projectAdapter}
          onClose={() => setPicker(null)}
          onSelect={(module) => {
            setTarget(module);
            setPicker(null);
          }}
        />
      )}

      {/* The real LibraryModal — opened by the FileField trigger buttons.
          Which slot it writes to is decided by `picker`. */}
      {hasLibrary && (picker === 'target' || picker === 'linkedDoc') && (
        <LibraryModal
          isOpen
          adapter={libraryAdapter!}
          onClose={() => setPicker(null)}
          onSelect={(entry) => {
            if (picker === 'target') setTarget(entry.id);
            else if (picker === 'linkedDoc') setLinkedDoc(entry.id);
            setPicker(null);
          }}
        />
      )}
    </Modal>
  );
}

/**
 * FileField — a trigger row that opens the LibraryModal. Shows the selected
 * file's id (or the placeholder) + a clear (×) button when something is set.
 * Not a combobox — the actual search happens in LibraryModal.
 */
function FileField({
  label,
  placeholder,
  onPick,
  onClear,
  disabled,
}: {
  label: string;
  placeholder: string;
  onPick: () => void;
  onClear: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="file-field">
      <button
        type="button"
        className="file-field-trigger"
        disabled={disabled}
        onClick={onPick}
      >
        <FileText size={14} />
        <span className="file-field-label">{label || placeholder}</span>
      </button>
      {label && !disabled && (
        <button
          type="button"
          className="file-field-clear"
          title="Clear"
          onClick={onClear}
        >
          <X size={12} weight="bold" />
        </button>
      )}
    </div>
  );
}
